import { useState } from 'react';

export type VinLookupStatus = 'idle' | 'loading' | 'success' | 'error';

export interface VinDetails {
  trim?: string;
  engine?: string;
  drivetrain?: string;
  bodyStyle?: string;
}

export interface VinLookupResult {
  vin: string;
  status: VinLookupStatus;
  message: string;
  details: VinDetails | null;
  setVin: (v: string) => void;
  lookup: () => Promise<void>;
  clear: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export function useVinLookup(
  onSuccess: (fields: { year: string; make: string; model: string; trim: string }) => void,
  onClear: () => void,
): VinLookupResult {
  const [vin, setVinRaw] = useState('');
  const [status, setStatus] = useState<VinLookupStatus>('idle');
  const [message, setMessage] = useState('');
  const [details, setDetails] = useState<VinDetails | null>(null);

  const setVin = (v: string) => {
    setVinRaw(v.toUpperCase());
    if (status !== 'idle') {
      setStatus('idle');
      setMessage('');
    }
  };

  const lookup = async () => {
    const cleanVin = vin.trim().toUpperCase();
    if (cleanVin.length !== 17) {
      setStatus('error');
      setMessage('VIN must be exactly 17 characters.');
      return;
    }
    setStatus('loading');
    setMessage('');
    setDetails(null);
    try {
      const res = await fetch(
        `https://vpic.nhtsa.dot.gov/api/vehicles/decodevinvalues/${cleanVin}?format=json`,
      );
      if (!res.ok) throw new Error('Network error');
      const data = await res.json();
      const result = data?.Results?.[0];
      if (!result || result.ErrorCode !== '0') {
        setStatus('error');
        setMessage('Could not decode this VIN. Please fill in the details manually.');
        return;
      }
      const year = result.ModelYear || '';
      const make = result.Make
        ? result.Make.charAt(0).toUpperCase() + result.Make.slice(1).toLowerCase()
        : '';
      const model = result.Model || '';
      if (!year || !make || !model) {
        setStatus('error');
        setMessage('VIN decoded but some details are missing. Please verify below.');
        return;
      }
      const decoded: VinDetails = {
        trim: result.Trim || undefined,
        engine: result.DisplacementL
          ? `${parseFloat(result.DisplacementL).toFixed(1)}L`
          : undefined,
        drivetrain: result.DriveType || undefined,
        bodyStyle: result.BodyClass || undefined,
      };
      setDetails(decoded);
      setStatus('success');
      setMessage(`Found: ${year} ${make} ${model}`);
      onSuccess({ year, make, model, trim: result.Trim || '' });
    } catch {
      setStatus('error');
      setMessage('VIN lookup failed. Please fill in the details manually.');
    }
  };

  const clear = () => {
    setVinRaw('');
    setStatus('idle');
    setMessage('');
    setDetails(null);
    onClear();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      lookup();
    }
  };

  return { vin, status, message, details, setVin, lookup, clear, onKeyDown };
}
